import {
  POS_CARD_WIDTH_KEY,
  POS_CARD_HEIGHT_KEY,
  POS_CARD_WIDTH_MIN,
  POS_CARD_WIDTH_MAX,
  POS_CARD_HEIGHT_MIN,
  POS_CARD_HEIGHT_MAX,
  POS_CARD_NAME_FONT_KEY,
  POS_CARD_PRICE_FONT_KEY,
  POS_CARD_DETAIL_FONT_KEY,
  POS_CARD_FONT_MIN,
  POS_CARD_FONT_MAX,
} from '../constants/posConst';

/**
 * One stored dimension, held to its range. 0 means "automatic", which is also
 * what anything absent, blank or out of range reads as — a bad value falls
 * back to the layout the grid has always had rather than to a broken one.
 *
 * @param {*} value - the stored figure
 * @param {number} min
 * @param {number} max
 * @returns {number} whole pixels, or 0 for automatic
 */
const dimension = (value, min, max) => {
  const size = Math.floor(Number(value));
  if (!Number.isFinite(size) || size < min || size > max) return 0;
  return size;
};

/**
 * This client's product card size and font sizes, from the merged config.
 *
 * @param {object} [config] - from useConfig()
 * @returns {{width: number, height: number, nameFont: number, priceFont: number, detailFont: number}}
 *   px, 0 = automatic
 */
export const posCardSize = (config) => ({
  width: dimension(config?.[POS_CARD_WIDTH_KEY], POS_CARD_WIDTH_MIN, POS_CARD_WIDTH_MAX),
  height: dimension(config?.[POS_CARD_HEIGHT_KEY], POS_CARD_HEIGHT_MIN, POS_CARD_HEIGHT_MAX),
  nameFont: dimension(config?.[POS_CARD_NAME_FONT_KEY], POS_CARD_FONT_MIN, POS_CARD_FONT_MAX),
  priceFont: dimension(config?.[POS_CARD_PRICE_FONT_KEY], POS_CARD_FONT_MIN, POS_CARD_FONT_MAX),
  detailFont: dimension(config?.[POS_CARD_DETAIL_FONT_KEY], POS_CARD_FONT_MIN, POS_CARD_FONT_MAX),
});

/**
 * The class names and CSS variables a product grid needs to honour the
 * configured card size. Spread onto the grid element alongside its own class.
 *
 * Both are empty on "automatic", so an unconfigured client's grid renders
 * exactly as it did before the setting existed.
 *
 * @param {object} [config] - from useConfig()
 * @returns {{className: string, style: object}}
 */
export const posCardGridAttrs = (config) => {
  const { width, height, nameFont, priceFont, detailFont } = posCardSize(config);
  const classes = [];
  const style = {};
  if (width > 0) {
    classes.push('pos-grid-fixed-w');
    style['--pos-card-w'] = `${width}px`;
  }
  if (height > 0) {
    classes.push('pos-grid-fixed-h');
    style['--pos-card-h'] = `${height}px`;
  }
  // Each font size is its own class, so leaving one blank keeps that text at
  // the stylesheet's size (including its smaller phone size).
  [
    [nameFont, 'pos-grid-fs-name', '--pos-card-fs-name'],
    [priceFont, 'pos-grid-fs-price', '--pos-card-fs-price'],
    [detailFont, 'pos-grid-fs-detail', '--pos-card-fs-detail'],
  ].forEach(([size, className, variable]) => {
    if (size <= 0) return;
    classes.push(className);
    style[variable] = `${size}px`;
  });
  return { className: classes.join(' '), style };
};

/**
 * Validates one typed dimension from the settings form.
 *
 * @param {string|number} raw - as typed; blank means automatic
 * @param {number} min
 * @param {number} max
 * @returns {{ok: boolean, value: number}} value is 0 for blank
 */
export const parsePosCardDimension = (raw, min, max) => {
  const typed = String(raw ?? '').trim();
  if (typed === '' || typed === '0') return { ok: true, value: 0 };
  const value = Number(typed);
  return { ok: Number.isInteger(value) && value >= min && value <= max, value };
};
